// src/pages/admin/AdminUpgrade.js — Upgrade de Plano
import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Stack, Chip,
  Divider, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiArrowUp, FiCheck, FiZap, FiBriefcase, FiAward
} from 'react-icons/fi';
import AppSidebar from '../../components/AppSidebar';
import api from '../../services/Api';

// ── Definição dos planos ──────────────────────────────────────────────────────
const PLANOS = [
  {
    key: 'STARTER',
    nome: 'Starter',
    preco: 'R$ 139',
    periodo: '/mês',
    cor: '#25d366',
    corBg: 'rgba(37,211,102,0.08)',
    corBorder: 'rgba(37,211,102,0.25)',
    icone: FiZap,
    descricao: 'Ideal para pequenas empresas que estão começando com o WhatsApp Business API.',
    recursos: [
      '1 Chip WhatsApp',
      '1 Agente + 1 Supervisor',
      'Health Score e Dashboard',
      'CRM Básico',
      'Monitoramento 24/7',
      'Suporte por e-mail',
    ],
  },
  {
    key: 'BUSINESS',
    nome: 'Business',
    preco: 'R$ 339',
    periodo: '/mês',
    cor: '#3b82f6',
    corBg: 'rgba(59,130,246,0.08)',
    corBorder: 'rgba(59,130,246,0.3)',
    icone: FiBriefcase,
    destaque: true,
    descricao: 'Para empresas em crescimento que precisam de mais capacidade e automação.',
    recursos: [
      '5 Chips WhatsApp',
      '5 Agentes + 1 Supervisor',
      'Supervisão em tempo real',
      'CRM Completo',
      'Automação de mensagens',
      'Suporte prioritário',
    ],
  },
  {
    key: 'ENTERPRISE',
    nome: 'Enterprise',
    preco: 'Sob consulta',
    periodo: '',
    cor: '#f59e0b',
    corBg: 'rgba(245,158,11,0.08)',
    corBorder: 'rgba(245,158,11,0.25)',
    icone: FiAward,
    descricao: 'Solução completa e personalizada para grandes operações e alto volume de mensagens.',
    recursos: [
      'Chips ilimitados',
      'Agentes ilimitados',
      'CRM Completo',
      'Automação de mensagens',
      'SLA garantido',
      'Gerente de conta dedicado',
      'Onboarding assistido',
    ],
  },
];

// PLUS (out/26) não tem card — é sob medida —, mas precisa estar na ordem:
// sem ele, o Starter apareceria como "upgrade" para quem está no PLUS.
const ORDEM_PLANOS = ['STARTER', 'PLUS', 'BUSINESS', 'ENTERPRISE'];

// Com assinatura valendo, a troca é na própria assinatura (POST
// /billing/change-plan). Out/26: antes todo clique abria um checkout novo, que
// criava uma SEGUNDA assinatura no Stripe — cobrada na hora, sem teste — e a
// primeira seguia cobrando.
const EM_VIGOR = ['ACTIVE', 'TRIALING', 'PAST_DUE'];

const dataCurta = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch { return ''; }
};

export default function AdminUpgrade() {
  const navigate  = useNavigate();
  const token     = localStorage.getItem('token');
  const user      = JSON.parse(localStorage.getItem('user') || '{}');

  const [planoAtual, setPlanoAtual]       = useState(null);
  const [assinatura, setAssinatura]       = useState({ status: null, fim: null });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingUpgrade, setLoadingUpgrade] = useState('');
  const [confirmar, setConfirmar]         = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });

  // ── Busca o plano atual da empresa ────────────────────────────────────────
  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    const fetchStatus = async () => {
      try {
        const res = await api.get('/billing/subscription-status', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPlanoAtual(res.data.plan_type || 'STARTER');
        setAssinatura({ status: res.data.subscription_status, fim: res.data.current_period_end });
      } catch {
        setPlanoAtual(user.plan || 'STARTER');
      } finally {
        setLoadingStatus(false);
      }
    };
    fetchStatus();
  }, [token, navigate, user.plan]);

  // ── Troca de plano ────────────────────────────────────────────────────────
  // Sem assinatura ainda: termina o cadastro (o servidor dá os 7 dias grátis
  // da primeira assinatura). Com assinatura: confirma e troca nela.
  const handleUpgrade = (planKey) => {
    if (planKey === 'ENTERPRISE') {
      window.open('mailto:contato@cettosystem.com.br?subject=Interesse no Plano Enterprise', '_blank');
      return;
    }
    setMsg({ type: '', text: '' });
    if (EM_VIGOR.includes(assinatura.status)) {
      setConfirmar(planKey);
      return;
    }
    navigate(`/register?retomar=1&plan=${planKey.toLowerCase()}`);
  };

  const trocarPlano = async () => {
    const planKey = confirmar;
    setLoadingUpgrade(planKey);
    try {
      const res = await api.post(
        '/billing/change-plan',
        { plan_type: planKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const nome = PLANOS.find(p => p.key === planKey)?.nome || planKey;
      setPlanoAtual(res.data.plan_type);
      try {
        localStorage.setItem('user', JSON.stringify({ ...user, plan: res.data.plan_type }));
      } catch { /* só o cache do menu */ }
      setMsg({ type: 'success', text: `Pronto: sua empresa está no plano ${nome}.` });
    } catch (err) {
      setMsg({
        type: 'error',
        text: err.response?.data?.detail || 'Não foi possível trocar o plano. Tente novamente.'
      });
    } finally {
      setLoadingUpgrade('');
      setConfirmar(null);
    }
  };

  // Determina se um plano é upgrade, atual ou downgrade
  const getRelacao = (planKey) => {
    if (!planoAtual) return 'upgrade';
    const idxAtual = ORDEM_PLANOS.indexOf(planoAtual);
    const idxAlvo  = ORDEM_PLANOS.indexOf(planKey);
    if (idxAlvo === idxAtual) return 'atual';
    if (idxAlvo > idxAtual)  return 'upgrade';
    return 'downgrade';
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <AppSidebar user={user} />

      <Box sx={{ flexGrow: 1, ml: { xs: 0, md: 'var(--sidebar-width, 260px)' }, transition: 'margin-left 0.25s ease', p: { xs: 2, md: 4 }, maxWidth: 1100 }}>

        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} mb={1}>
          <Button
            startIcon={<FiArrowLeft />}
            onClick={() => navigate('/dashboard')}
            sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
          >
            Voltar
          </Button>
          <Divider orientation="vertical" flexItem />
          <FiArrowUp size={22} color="#3b82f6" />
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0f172a">
              Upgrade de Plano
            </Typography>
            <Typography variant="body2" color="#64748b">
              Escolha o plano ideal para o crescimento da sua empresa
            </Typography>
          </Box>
        </Stack>

        {/* Plano atual */}
        {!loadingStatus && planoAtual && (
          <Box sx={{ mb: 3 }}>
            <Chip
              label={`Plano atual: ${planoAtual}`}
              sx={{ bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 700, fontSize: '0.8rem', height: 28 }}
            />
          </Box>
        )}

        {msg.text && (
          <Alert severity={msg.type || 'info'} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMsg({ type: '', text: '' })}>
            {msg.text}
          </Alert>
        )}

        {/* Cards dos planos */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          alignItems="stretch"
        >
          {PLANOS.map((plano) => {
            const relacao   = getRelacao(plano.key);
            const isAtual   = relacao === 'atual';
            const isDestaque = plano.destaque && !isAtual;
            const PlanIcon  = plano.icone;

            return (
              <Paper
                key={plano.key}
                elevation={isDestaque ? 4 : 0}
                sx={{
                  flex: 1,
                  borderRadius: 3,
                  border: isAtual
                    ? `2px solid ${plano.cor}`
                    : isDestaque
                      ? `2px solid ${plano.cor}`
                      : '1px solid #e2e8f0',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: isAtual ? 'none' : 'translateY(-4px)',
                    boxShadow: isAtual ? 'none' : '0 12px 40px rgba(0,0,0,0.1)',
                  }
                }}
              >
                {/* Badge "Mais Popular" */}
                {isDestaque && (
                  <Box sx={{
                    bgcolor: plano.cor, py: 0.8, textAlign: 'center',
                  }}>
                    <Typography variant="caption" fontWeight={800} color="white" letterSpacing={1}>
                      MAIS POPULAR
                    </Typography>
                  </Box>
                )}

                {/* Badge "Plano Atual" */}
                {isAtual && (
                  <Box sx={{
                    bgcolor: plano.cor, py: 0.8, textAlign: 'center',
                  }}>
                    <Typography variant="caption" fontWeight={800} color="white" letterSpacing={1}>
                      SEU PLANO ATUAL
                    </Typography>
                  </Box>
                )}

                <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Ícone e nome */}
                  <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: '10px',
                      bgcolor: plano.corBg, display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <PlanIcon size={20} color={plano.cor} />
                    </Box>
                    <Typography variant="h6" fontWeight={800} color="#0f172a">
                      {plano.nome}
                    </Typography>
                  </Stack>

                  {/* Preço */}
                  <Stack direction="row" alignItems="baseline" spacing={0.5} mb={1}>
                    <Typography variant="h4" fontWeight={900} color={plano.cor}>
                      {plano.preco}
                    </Typography>
                    {plano.periodo && (
                      <Typography variant="body2" color="#94a3b8" fontWeight={500}>
                        {plano.periodo}
                      </Typography>
                    )}
                  </Stack>

                  {/* Descrição */}
                  <Typography variant="body2" color="#64748b" mb={2.5} lineHeight={1.6}>
                    {plano.descricao}
                  </Typography>

                  <Divider sx={{ mb: 2.5 }} />

                  {/* Recursos */}
                  <Stack spacing={1.2} flexGrow={1}>
                    {plano.recursos.map(recurso => (
                      <Stack key={recurso} direction="row" spacing={1.2} alignItems="center">
                        <FiCheck size={15} color={plano.cor} strokeWidth={2.5} />
                        <Typography variant="body2" color="#334155" fontWeight={500}>
                          {recurso}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>

                  {/* Botão de ação */}
                  <Box mt={3}>
                    {isAtual ? (
                      <Button
                        fullWidth
                        variant="outlined"
                        disabled
                        sx={{
                          borderColor: plano.cor, color: plano.cor,
                          textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1.2,
                          '&.Mui-disabled': { borderColor: plano.cor, color: plano.cor, opacity: 0.6 }
                        }}
                      >
                        Plano Atual
                      </Button>
                    ) : plano.key === 'ENTERPRISE' ? (
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleUpgrade(plano.key)}
                        sx={{
                          borderColor: plano.cor, color: plano.cor,
                          textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1.2,
                          '&:hover': { bgcolor: plano.corBg, borderColor: plano.cor }
                        }}
                      >
                        Falar com Especialista
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleUpgrade(plano.key)}
                        disabled={!!loadingUpgrade}
                        startIcon={
                          loadingUpgrade === plano.key
                            ? <CircularProgress size={16} color="inherit" />
                            : <FiArrowUp size={16} />
                        }
                        sx={{
                          bgcolor: plano.cor, color: 'white',
                          textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1.2,
                          '&:hover': { bgcolor: plano.cor, filter: 'brightness(0.9)' }
                        }}
                      >
                        {loadingUpgrade === plano.key
                          ? 'Trocando...'
                          : relacao === 'upgrade' ? `Fazer Upgrade` : `Mudar para ${plano.nome}`}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Stack>

        {/* Nota de rodapé */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="#94a3b8">
            A troca vale na hora e nada é cobrado no momento: no teste grátis, a mensalidade do plano novo
            começa no fim do teste; depois dele, a diferença proporcional entra na próxima fatura.
            Sem multas ou taxas de cancelamento. Dúvidas? Entre em contato: <strong>contato@cettosystem.com.br</strong>
          </Typography>
        </Box>
      </Box>

      <DialogTrocaPlano
        plano={PLANOS.find(p => p.key === confirmar)}
        relacao={confirmar ? getRelacao(confirmar) : null}
        assinatura={assinatura}
        trocando={!!loadingUpgrade}
        onFechar={() => setConfirmar(null)}
        onConfirmar={trocarPlano}
      />
    </Box>
  );
}

// O que acontece com a cobrança, dito antes do clique: no teste nada é
// cobrado até o fim dele; depois, a diferença dos dias que faltam vai
// proporcional na próxima fatura (crédito, no downgrade).
function DialogTrocaPlano({ plano, relacao, assinatura, trocando, onFechar, onConfirmar }) {
  if (!plano) return null;
  const fim = dataCurta(assinatura.fim);
  const emTeste = assinatura.status === 'TRIALING';
  let texto;
  if (emTeste) {
    texto = `O plano ${plano.nome} vale a partir de agora e o seu teste grátis continua${fim ? ` até ${fim}` : ''}. `
      + `Nada é cobrado hoje: no fim do teste, a mensalidade passa a ser de ${plano.preco}${plano.periodo}.`;
  } else if (relacao === 'downgrade') {
    texto = `O plano ${plano.nome} vale a partir de agora, com os limites dele. `
      + `O valor dos dias que faltam${fim ? ` até ${fim}` : ''} vira crédito na sua próxima fatura, `
      + `e a mensalidade passa a ser de ${plano.preco}${plano.periodo}.`;
  } else {
    texto = `O plano ${plano.nome} vale a partir de agora. Nada é cobrado hoje: a diferença proporcional `
      + `aos dias que faltam${fim ? ` até ${fim}` : ''} entra na sua próxima fatura, `
      + `e a mensalidade passa a ser de ${plano.preco}${plano.periodo}.`;
  }
  return (
    <Dialog open onClose={trocando ? undefined : onFechar} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Mudar para o plano {plano.nome}?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="#334155" lineHeight={1.7}>{texto}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onFechar} disabled={trocando} sx={{ textTransform: 'none', color: '#64748b' }}>
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={onConfirmar}
          disabled={trocando}
          startIcon={trocando ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: plano.cor, '&:hover': { bgcolor: plano.cor, filter: 'brightness(0.9)' } }}
        >
          {trocando ? 'Trocando...' : `Mudar para ${plano.nome}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
